import { compact } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { ContactJson } from 'app/types/entities'
import type { SystemListBand, SystemListDef } from 'app/types/navigation'
import { useWorkspace } from 'app/utils'
import { bandSearchTerms, type TreatmentBandId } from 'app/components/action-center/treatmentBands'
import { SYSTEM_LISTS, TREATMENT_LIST, familySearchTerms, systemListGate } from 'lib/systemLists'
import { TREATMENT_POPULATION_WITH_BOOKED } from 'lib/treatmentStages'

export type RuleListStoreId = `system:hygiene` | `system:treatment`

export type RuleListRow = {
  def: SystemListDef
  band?: SystemListBand
  /** The system pipeline that stores this list's stage, when there is one. */
  storeId?: RuleListStoreId
  /** The contact's current membership stage on `storeId`. */
  stage?: string
}

/**
 * Where each rule list keeps a person's progress. Active Leads is missing on purpose:
 * `system:leads` is not membership-backed, its board moves `leadStatus` instead.
 */
const STAGE_STORE: Readonly<Record<string, RuleListStoreId>> = {
  'hygiene-recall': `system:hygiene`,
  'unscheduled-treatment': `system:treatment`,
}

/**
 * Whether a contact is on each system list, answered by the server.
 *
 * One `contacts` count per band (per family where a list has no bands), scoped to the
 * contact by id and carrying the list's own search terms verbatim, so a row can never
 * drift from the board it links to: the browser never re-implements a rule.
 */
export function useRuleListMembership(contact: ContactJson | undefined): { rows: RuleListRow[]; loading: boolean } {
  const contactId = contact?.id
  const memberships = contact?.pipelines
  // A booking keeps the person on Unscheduled Treatment even when they owe nothing, so the value term widens.
  const treatmentBooked = contact?.treatmentStatus === TREATMENT_LIST.bookedStage
  // The workspace as a boolean: it is only a gate here, and a fresh object each render
  // would restart the reads on every render.
  const hasWorkspace = !!useWorkspace()
  const needsActionDateEnabled = !!useFeatureToggle(`needs-action-date`)
  // The sidebar's own reading of whether the workspace can serve Unscheduled Treatment.
  const { treatmentGate } = usePmsIntegration()
  const [rows, setRows] = useState<RuleListRow[]>([])
  const [loading, setLoading] = useState(!!contactId)

  // A stage move rewrites the memberships, and an exit stage can take the person out of
  // the rule's own search, so the reads run again rather than leaving a stale row behind.
  const membershipKey = useMemo(
    () =>
      compact(memberships)
        .map((membership) => `${membership.id}::${membership.stage ?? ``}`)
        .join(`|`),
    [memberships],
  )

  useEffect(() => {
    if (!contactId) {
      setRows([])
      setLoading(false)
      return
    }
    // Waits for the integration read rather than running every count twice when it lands.
    if (!treatmentGate) return
    let cancelled = false
    const opts = { needsActionDateEnabled }
    // The Hygiene Recall board holds the booked in its Scheduled stage, so its membership counts them too.
    const hygieneOpts = { ...opts, includeBooked: true }

    const countMatches = (terms: string[]) =>
      gqlFetchList(`contacts`, { search: `id:"${contactId}" ${terms.join(` `)}`, pagination: { limit: 0 } }, `id`, {
        suppressErrorToast: true,
      })
        .then((result) => result.total)
        .catch(() => undefined)

    const termsFor = (def: SystemListDef, band: SystemListBand | undefined) => {
      if (def === TREATMENT_LIST && treatmentBooked) {
        return band
          ? bandSearchTerms(band.id as TreatmentBandId, undefined, TREATMENT_POPULATION_WITH_BOOKED)
          : [TREATMENT_POPULATION_WITH_BOOKED]
      }
      return familySearchTerms(def, band, def.listKind === `hygiene` ? hygieneOpts : opts)
    }

    const rowFor = async (def: SystemListDef): Promise<RuleListRow | undefined> => {
      const bands = def.bands
      const totals = await Promise.all((bands ?? [undefined]).map((band) => countMatches(termsFor(def, band))))
      const matched = totals.findIndex((total) => (total ?? 0) > 0)
      if (matched < 0) return undefined
      const storeId = STAGE_STORE[def.slug]
      return {
        def,
        band: bands?.[matched],
        storeId,
        stage: storeId ? (compact(memberships).find((m) => m.id === storeId)?.stage ?? undefined) : undefined,
      }
    }

    // The sidebar row's own gates: a row never links to a list the sidebar hides or locks.
    const gateOpen = (def: SystemListDef) => {
      const gate = systemListGate(def, { hasWorkspace, treatmentGate })
      return !!gate.if && !gate.isLocked
    }

    setLoading(true)
    Promise.all(SYSTEM_LISTS.filter((def) => gateOpen(def)).map(rowFor)).then((found) => {
      if (cancelled) return
      setRows(compact(found))
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [contactId, membershipKey, treatmentBooked, needsActionDateEnabled, hasWorkspace, treatmentGate])

  return { rows, loading }
}
